import {
  Button,
  Card,
  Conditional,
  Input,
  Select,
  Table,
  TablePagination,
} from "@result/rui/components";
import {
  useForm,
  useNotifications,
  useTable,
  useUpdateOnlyEffect,
} from "@result/rui/hooks";
import { ApiFilter, ApiPage } from "@result/rui/utils/api";
import { toHumanReadableDate } from "@result/rui/utils/date";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import * as yup from "yup";

import DateInput from "#/components/DateInput";
import PageHeader from "#/components/PageHeader.jsx";
import SelectLookupTableItem from "#/components/SelectLookupTableItem";
import DataLayerSearchSelect from "#/components/data-layer/SearchSelect";
import PrintContent from "#/components/pdf/PrintContent";
import * as DLD from "#/def/data-layer";
import * as LTD from "#/def/lookup-tables.ident.gen";
import { useDataLayerQuery } from "#/hooks/useDataLayer";
import yesNoOptions from "#/model/YesNoOptions";
import { filterLastnostiPvBySpecies } from "#/pages/breeding-values/BreedingValueUtils";
import BreedingValuesPresets from "#/pages/breeding-values/BreedingValuesPresets";
import KodaIzracunSelect from "#/pages/breeding-values/KodaIzracunSelect";
import { useBreedingValuesKodaIzracunQuery } from "#/pages/breeding-values/useBreedingValuesKodaIzracunQuery";
import { useBreedingValuesRankQuery } from "#/pages/breeding-values/useBreedingValuesRankQuery";
import * as species from "#/utils/SpeciesScope";

const BreedingValuesRankPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const trendOptions = useMemo(
    () => [
      {
        id: "POZITIVEN",
        label: "pozitiven",
      },
      {
        id: "NEGATIVEN",
        label: "negativen",
      },
      {
        id: "BREZ",
        label: "brez",
      },
      {
        id: "NOVA_ZIVAL",
        label: "nova žival",
      },
    ],
    []
  );

  const defaultLastnost = {
    SHEEPS: "OVCA_KODPV_168",
    GOATS: "OVCA_KODPV_168",
    PIGS: "012",
    HORSES: "KOPITAR_KODPV_1",
    CATTLE: "GOVEDO_KODPV_168",
  };

  const officialityOptions = useMemo(() => yesNoOptions(t), [t]);

  const supportedSpecies = species.get();
  const kodaIzracunQuery = useBreedingValuesKodaIzracunQuery();
  const { addErrorNotification, addWarningNotification } = useNotifications();
  const [lastFetchValues, setLastFetchValues] = useState({});
  const [pedigreeLoadingPlemenskaZivalId, setPedigreeLoadingPlemenskaZivalId] =
    useState(null);

  const rangQuery = useBreedingValuesRankQuery();
  const kodePvQuery = useDataLayerQuery(DLD.PLEMENSKA_VREDNOST_KODA);
  const zivalIdentifikatorQuery = useDataLayerQuery(DLD.ZIVAL_IDENTIFIKATOR);
  const breedQuery = useDataLayerQuery(DLD.BREEDS);

  useEffect(() => {
    if (kodaIzracunQuery.result?.list == null && supportedSpecies.length > 0) {
      kodaIzracunQuery.query({ supportedSpecies });
    }
    if (supportedSpecies.length > 0) {
      kodePvQuery.query({
        page: new ApiPage().page(0).size(10000),
        filter: species.filter,
      });
      getField("kodaPvPrimerjalni").setValue(
        defaultLastnost[supportedSpecies[0]]
      );
    }
    breedQuery.query({
      page: new ApiPage().page(0).size(1000),
      filter: species.filter(),
    });
  }, [supportedSpecies]);
  const breeds = breedQuery.result?.content ?? [];

  const { sort, onSort } = useTable(
    [],
    () => {},
    () => {}
  );

  useUpdateOnlyEffect(() => {
    searchBreedingValues(values, { page: 0 });
  }, [sort]);

  const searchBreedingValues = (values, { page = 0 }) => {
    setLastFetchValues(values);
    const lastnostiFilter = Object.keys(values.lastnostiFilter)
      .filter((k) => tableKodePv.includes(k))
      .map((key) => {
        const value = values.lastnostiFilter[key];
        if (value.min || value.max || value.tocnostMin || value.tocnostMax) {
          return {
            lastnostsifra: key,
            ...value,
          };
        }
        return null;
      })
      .filter(Boolean);

    const kodePv = new Set(values.kodePv);
    kodePv.add(values.kodaPvPrimerjalni);

    const sortField = sort?.toJSON()?.orders?.[0]?.property;
    let sortFieldName = null;
    let sortKodaPv = null;
    if (sortField != null) {
      if (["TOCNOST", "RANG", "DATUM_ROJSTVA"].includes(sortField)) {
        sortFieldName = sortField;
      }

      if (sortField.endsWith("-tocnost")) {
        sortKodaPv = sortField.replace("-tocnost", "");
      }
    }
    rangQuery
      .query({
        body: {
          kodaIzracun: values.kodobr,
          kodaIzracunPrimerjalni: values.kodobrPrimerjalni,
          kodaPvPrimerjalni: values.kodaPvPrimerjalni,
          zivalId: values.zivalId,
          spolSifra: values.spolSifra,
          kodePv: Array.from(kodePv),
          lastnostiFilter: lastnostiFilter,
          trend: values.trend,
          uradnost: values.uradnost == null ? null : values.uradnost === "true",
          rojstvoOd: values.rojstvoOd,
          rojstvoDo: values.rojstvoDo,
          populacija: values.populacija === "VSE" ? null : values.populacija,
          lokacija: values.lokacija,
          page: page,
          pageSize: 100,
          sort: sortFieldName,
          sortKodaPv,
        },
      })
      .catch((e) => {
        console.log("Error fetching breeding values", e);
        addErrorNotification(t("breedingValues.errorFetching"));
      });
  };

  const [isInitialFetchTriggered, setIsInitialFetchTriggered] = useState(false);
  const [initialValues, setInitialValues] = useState({});

  useEffect(() => {
    if (
      kodaIzracunQuery.result?.list == null ||
      kodaIzracunQuery.result.list.length === 0
    ) {
      return;
    }
    // TODO: remove temp popravek da se pokažejo vrednosti takoj na začetku pri govedu
    if (supportedSpecies[0] === "GOVEDO") {
      getField("kodobr").setValue("2602");
      getField("kodobrPrimerjalni").setValue("2601");
      return;
    }
    getField("kodobr").setValue(kodaIzracunQuery.result.list[0]);
    getField("kodobrPrimerjalni").setValue(
      kodaIzracunQuery.result.list[1] ?? kodaIzracunQuery.result.list[0]
    );
  }, [kodaIzracunQuery.result]);

  const validationSchema = {
    populacija: yup.string().required(),
    kodobr: yup.string().required(),
    kodobrPrimerjalni: yup.string().required(),
    kodaPvPrimerjalni: yup.string().required(),
  };

  const { getField, submit, values, setValues } = useForm({
    initialValues: {
      lastnostiFilter: {},
      populacija: "VSE",
    },
    schema: validationSchema,
    onSubmit: searchBreedingValues,
  });

  useEffect(() => {
    if (
      !isInitialFetchTriggered &&
      values.populacija != null &&
      values.kodobr != null &&
      values.kodobrPrimerjalni != null &&
      values.kodaPvPrimerjalni != null
    ) {
      setIsInitialFetchTriggered(true);
      setInitialValues(values);
      submit(values);
    }
  }, [
    values.populacija,
    values.kodobr,
    values.kodobrPrimerjalni,
    values.kodaPvPrimerjalni,
  ]);

  const tableKodePv = useMemo(() => {
    let kodePv = [...(lastFetchValues.kodePv ?? [])].filter(
      (kodaPv) => kodaPv !== lastFetchValues.kodaPvPrimerjalni
    );
    kodePv = [lastFetchValues.kodaPvPrimerjalni, ...kodePv];
    return kodePv.filter(Boolean);
  }, [lastFetchValues.kodaPvPrimerjalni, lastFetchValues.kodePv]);

  const filterTableKodePv = useMemo(() => {
    let kodePv = [...(values.kodePv ?? [])].filter(
      (kodaPv) => kodaPv !== values.kodaPvPrimerjalni
    );
    kodePv = [values.kodaPvPrimerjalni, ...kodePv];
    return kodePv.filter(Boolean);
  }, [values.kodaPvPrimerjalni, values.kodePv]);

  const breedingValueTableColumns = useMemo(() => {
    return tableKodePv.flatMap((pvType, index) => {
      const columns = [
        {
          id: `${pvType}-PV`,
          name: t("PV"),
          isSortable: false,
          cellComponent: (rank) => {
            const breedingValue = rank.plemenskeVrednosti[pvType];
            return (
              <div
                className={
                  index === 0
                    ? "has-background-pv-highlight has-radius-small"
                    : ""
                }>
                {breedingValue?.standardiziranaPv ?? "-"}
              </div>
            );
          },
        },
        {
          id: index === 0 ? "TOCNOST" : `${pvType}-tocnost`,
          name: t("toč."),
          cellComponent: (rank) => {
            const breedingValue = rank.plemenskeVrednosti[pvType];
            return breedingValue?.tocnostPv ?? "-";
          },
        },
      ];
      if (index === 0) {
        columns.push({
          id: `${pvType}-PA`,
          isSortable: false,
          name: t("PA"),
          cellComponent: (rank) => {
            const breedingValue = rank.plemenskeVrednosti[pvType];
            return breedingValue?.absolutnaPv ?? "-";
          },
        });
      }
      return columns;
    });
  }, [tableKodePv]);

  const showPedigree = (identifikatorZivali) => {
    setPedigreeLoadingPlemenskaZivalId(identifikatorZivali);
    zivalIdentifikatorQuery
      .query({
        filter: new ApiFilter()
          .equal("vrednost", identifikatorZivali)
          .in("tip", ["USESNA_STEVILKA", "INTERBUL"]),
      })
      .then((result) => {
        if (result?.content?.length === 0) {
          addWarningNotification(
            t("breedingValues.animalNotFoundWithIdentifier", {
              identifier: identifikatorZivali,
            })
          );
          return;
        }
        const animalId = result.content[0].zivalId;
        if (animalId) {
          navigate(`/animal/${animalId}/pedigree`);
        }
      })
      .catch((_) => {
        addErrorNotification(t("breedingValues.errorFetchingAnimalData"));
      })
      .finally(() => {
        setPedigreeLoadingPlemenskaZivalId(null);
      });
  };

  const RankTable = ({ isPrint = false }) => {
    return (
      <>
        <Conditional condition={!isPrint}>
          <div className="buttons is-right mb-2">
            <Button icon="icon-file-pdf" onClick={() => window.print()} />
          </div>
        </Conditional>

        <Table
          data={rangQuery.result?.content ?? []}
          sort={sort}
          onSort={(newSort) => {
            const currentSortProperty = sort.toJSON()?.orders?.[0]?.property;
            if (
              currentSortProperty == null ||
              newSort !== currentSortProperty
            ) {
              onSort(newSort);
            } else {
              onSort(null);
            }
          }}
          headerElement={(header) => {
            return (
              <>
                <tr className="has-dividers">
                  <th>Rang</th>
                  <th colSpan={3}>% rang</th>
                  <th colSpan={isPrint ? 5 : 6}>Žival</th>
                  {tableKodePv.map((pvType, index) => {
                    return (
                      <th colSpan={index === 0 ? 3 : 2} key={pvType}>
                        {(kodePvQuery.result?.content ?? []).find(
                          (kodaPv) => kodaPv.sifra === pvType
                        )?.naziv?.sl ?? pvType}
                      </th>
                    );
                  })}
                  <th colSpan={3}>Oče</th>
                  <th colSpan={2}>Status</th>
                  <th>Lokacija</th>
                </tr>
                {header}
              </>
            );
          }}
          columns={[
            {
              id: "rangAbsolutno",
              name: t("breedingValues.columns.absoluteRang"),
              cellComponent: (rank) => {
                const breedingValue =
                  rank.plemenskeVrednosti?.[
                    rangQuery.result?.kodaPvPrimerjalni
                  ];
                return breedingValue?.rangAbsolutno ?? "-";
              },
            },
            {
              id: "trend",
              isSortable: false,
              name: t("breedingValues.columns.trend"),
              cellComponent: (breedingValue) => {
                let backgroundColor;
                if (breedingValue.trend == null) {
                  backgroundColor = "pv-highlight";
                } else if (breedingValue.trend === 0) {
                  backgroundColor = "trend-unchanged";
                } else if (breedingValue.trend > 0) {
                  backgroundColor = "trend-positive";
                } else {
                  backgroundColor = "trend-negative";
                }

                return (
                  <div
                    className={`has-background-${backgroundColor} has-radius-small trend-badge`}>
                    {breedingValue.trend ?? "-"}
                  </div>
                );
              },
            },
            {
              id: "newRang",
              isSortable: false,
              name: t("breedingValues.columns.newRank"),
              cellComponent: (rank) => {
                const breedingValue =
                  rank.plemenskeVrednosti?.[values.kodaPvPrimerjalni];
                return breedingValue?.rangProcentno ?? "-";
              },
            },
            {
              id: "oldRang",
              isSortable: false,
              name: t("breedingValues.columns.oldRank"),
              cellComponent: (rank) => {
                return rank?.plemenskaVrednostPrimerjalni?.rangProcentno ?? "-";
              },
            },
            ...(isPrint
              ? []
              : [
                  {
                    id: "rodovnik",
                    isSortable: false,
                    cellComponent: (rank) => {
                      if (supportedSpecies?.[0] === "CATTLE") {
                        return "";
                      }
                      const isLoading =
                        pedigreeLoadingPlemenskaZivalId ===
                        rank.plemenskaZival?.eksterniEnolicniIdentifikator;
                      if (isLoading) {
                        return <span className="loader"></span>;
                      }

                      return (
                        <span
                          className="has-radius-small has-background-light py-1 px-2"
                          data-tooltip={t("breedingValues.showPedigree")}
                          onClick={() => {
                            showPedigree(
                              rank.plemenskaZival?.eksterniEnolicniIdentifikator
                            );
                          }}>
                          R
                        </span>
                      );
                    },
                  },
                ]),
            {
              id: "zivalId",
              isSortable: false,
              name: supportedSpecies?.[0] === "HORSES" ? "ŽŠ" : "ID",
              cellComponent: (rang) =>
                rang.plemenskaZival?.eksterniEnolicniIdentifikator,
            },
            {
              id: "pedigreeNumber",
              isSortable: false,
              name: t("breedingValues.columns.pedigreeNum"),
              cellComponent: (rang) =>
                rang.plemenskaZival?.rodovniska_stevilka ?? "-",
            },
            {
              id: "pasma",
              isSortable: false,
              name: t("breedingValues.columns.breed"),
              cellComponent: (rang) => {
                const breedCode = rang.plemenskaZival?.pasmaSifra;
                if (breedCode == null) {
                  return "-";
                }
                const breed = breeds.find((breed) => breed.sifra === breedCode);
                return breed?.naziv?.sl ?? breedCode;
              },
            },
            {
              id: "ime",
              isSortable: false,
              name: t("breedingValues.columns.name"),
              cellComponent: (rang) => rang.plemenskaZival?.ime ?? "-",
            },
            {
              id: "DATUM_ROJSTVA",
              name: t("breedingValues.columns.birthDate"),
              cellComponent: (rang) =>
                rang.plemenskaZival?.datumRojstva
                  ? toHumanReadableDate(rang.plemenskaZival?.datumRojstva)
                  : "-",
            },
            ...breedingValueTableColumns,
            {
              id: "fatherId",
              isSortable: false,
              name: supportedSpecies?.[0] === "HORSES" ? "ŽŠ" : "ID",
              cellComponent: (rang) =>
                rang.plemenskaZival?.oce_identifikator ??
                rang.plemenskaZival?.oceIdentifikator ??
                "-",
            },
            {
              id: "fatherName",
              isSortable: false,
              name: t("breedingValues.columns.name"),
              cellComponent: (rang) =>
                rang.plemenskaZival?.oce_usesna_stevilka ?? "-",
            },
            {
              id: "fatherBirthDate",
              isSortable: false,
              name: t("breedingValues.columns.birthDate"),
              cellComponent: (rang) =>
                rang.plemenskaZival?.oce_zd_rojstvo_datum ?? "-",
            },
            {
              id: "officiality",
              isSortable: false,
              name: t("breedingValues.columns.officiality"),
              cellComponent: (rank) => {
                const breedingValue =
                  rank.plemenskeVrednosti?.[
                    rangQuery.result?.kodaPvPrimerjalni
                  ];
                if (breedingValue?.uradnostVrednosti == null) {
                  return "-";
                }
                return breedingValue.uradnostVrednosti ? (
                  <span className="icon icon-check is-success is-small"></span>
                ) : (
                  <span className="icon icon-xmark is-danger is-small"></span>
                );
              },
            },
            {
              id: "snp",
              isSortable: false,
              name: t("breedingValues.columns.snp"),
            },
            {
              id: "locationId",
              isSortable: false,
              name: t("breedingValues.columns.locationId"),
              cellComponent: (rank) => {
                return rank.plemenskaZival?.trenutna_lokacija_kmg_mid ?? "-";
              },
            },
          ]}
        />
      </>
    );
  };

  return (
    <>
      <form
        className="breeding-values-page has-table-sortable"
        onSubmit={submit}>
        <PageHeader
          title={t("breedingValues.rank")}
          subtitle={t("breedingValues.rank.description")}
          backURL=".."
        />
        <BreedingValuesPresets
          storageKey="BREEDING_VALUES_RANK_PRESETS"
          values={values}
          onLoad={(newValues) => {
            setValues(newValues);
            searchBreedingValues(newValues, { page: 0 });
          }}
        />
        <div className="fixed-grid has-1-cols-mobile has-2-cols">
          <div className="grid">
            <Card title={t("breedingValues.parameters")} className="m-0">
              <div className="is-flex is-flex-direction-column is-gap-2 is-justify-content-space-between">
                <div className="fixed-grid has-2-cols">
                  <div className="grid">
                    <KodaIzracunSelect
                      label={t("breedingValues.calculation")}
                      labelType="floating"
                      selectionMode="single"
                      formField={getField("kodobr")}
                    />
                    <KodaIzracunSelect
                      label={t("breedingValues.calculationComparison")}
                      formField={getField("kodobrPrimerjalni")}
                      selectionMode="single"
                      labelType="floating"
                    />
                    <DataLayerSearchSelect
                      ident={DLD.PLEMENSKA_VREDNOST_KODA}
                      queryFields={["sifra", "naziv.sl"]}
                      optionId="sifra"
                      optionLabel={(koda) =>
                        [koda.naziv?.sl, koda.sifra]
                          .filter(Boolean)
                          .join(" - ")
                          .toUpperCase()
                      }
                      filter={() => filterLastnostiPvBySpecies()}
                      optionValue="sifra"
                      label={t("breedingValues.breedingValueCriteria")}
                      formField={getField("kodaPvPrimerjalni")}
                      labelType="floating"
                    />
                    <SelectLookupTableItem
                      ident={LTD.SifPlemVredKodaPopulacije}
                      label={t("breedingValues.populationCode")}
                      labelType="floating"
                      formField={getField("populacija")}
                    />
                    <DataLayerSearchSelect
                      ident={DLD.PLEMENSKA_VREDNOST_KODA}
                      queryFields={["sifra", "naziv.sl"]}
                      optionId="sifra"
                      optionLabel={(koda) =>
                        [koda.naziv?.sl, koda.sifra]
                          .filter(Boolean)
                          .join(" - ")
                          .toUpperCase()
                      }
                      filter={() => filterLastnostiPvBySpecies()}
                      optionValue="sifra"
                      label={t("breedingValues.breedingValueCodes")}
                      formField={getField("kodePv")}
                      labelType="floating"
                      selectionMode="multiple"
                    />
                    <SelectLookupTableItem
                      ident={LTD.SifSpol}
                      formField={getField("spolSifra")}
                      labelType="floating"
                    />
                    <Select
                      options={officialityOptions}
                      formField={getField("uradnost")}
                      optionId="id"
                      optionLabel="label"
                      optionValue="id"
                      label={t("breedingValues.officiality")}
                      labelType="floating"
                    />
                    <Select
                      options={trendOptions}
                      formField={getField("trend")}
                      optionId="id"
                      optionValue="id"
                      optionLabel="label"
                      label={t("breedingValues.trend")}
                      labelType="floating"
                    />
                    <DateInput
                      label={t("breedingValues.birthFrom")}
                      labelType="floating"
                      formField={getField("rojstvoOd")}
                    />
                    <DateInput
                      label={t("breedingValues.to")}
                      labelType="floating"
                      formField={getField("rojstvoDo")}
                    />
                    <Input
                      label={
                        supportedSpecies?.[0] === "HORSES"
                          ? t("animal.lifeNumber")
                          : t("animalId")
                      }
                      formField={getField("zivalId")}
                      labelType="floating"
                    />
                    <Input
                      type="number"
                      formField={getField("lokacija")}
                      label={t("breedingValues.location")}
                      labelType="floating"
                    />
                  </div>
                </div>
                <div className="is-flex is-justify-content-space-between">
                  <div>
                    <Button
                      label={t("common.search")}
                      isSubmit={true}
                      isLoading={rangQuery.isLoading}
                      color="primary"
                    />
                  </div>
                  <Button
                    label={t("common.reset")}
                    onClick={() => {
                      setValues(initialValues);
                      submit(initialValues);
                    }}
                    isLoading={rangQuery.isLoading}
                  />
                </div>
              </div>
            </Card>
            <Card
              title={t("breedingValues.limitOutput")}
              className="breeding-values-rank-pv-filter">
              <table className="table">
                <thead>
                  <tr>
                    <th></th>
                    <th colSpan={2}>PV</th>
                    <th colSpan={2}>Točnost</th>
                    <th></th>
                  </tr>
                  <tr>
                    <th></th>
                    <th>Min</th>
                    <th>Max</th>
                    <th>Min</th>
                    <th>Max</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {filterTableKodePv.map((code) => (
                    <tr key={"filter-" + code} className="breeding-value-label">
                      <td>
                        {(kodePvQuery.result?.content ?? []).find(
                          (kodaPv) => kodaPv.sifra === code
                        )?.naziv?.sl ?? code}
                      </td>
                      <td>
                        <Input
                          formField={getField(`lastnostiFilter[${code}].min`)}
                        />
                      </td>
                      <td>
                        <Input
                          formField={getField(`lastnostiFilter[${code}].max`)}
                        />
                      </td>
                      <td>
                        <Input
                          formField={getField(
                            `lastnostiFilter[${code}].tocnostmin`
                          )}
                        />
                      </td>
                      <td>
                        <Input
                          formField={getField(
                            `lastnostiFilter[${code}].tocnostmax`
                          )}
                        />
                      </td>
                      <td>
                        <Conditional
                          condition={values.kodaPvPrimerjalni !== code}>
                          <span
                            className="icon icon-circle-x is-clickable"
                            onClick={() => {
                              const newFilter = {
                                ...values.lastnostiFilter,
                              };
                              delete newFilter[code];
                              getField("lastnostiFilter").setValue(newFilter);

                              let newKodePv = [
                                ...(values.kodePv ?? []).filter(
                                  (item) => item !== code
                                ),
                              ];
                              getField("kodePv").setValue(newKodePv);
                            }}></span>
                        </Conditional>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          </div>
        </div>
        <Card>
          <RankTable isPrint={false} />
          <TablePagination
            onPage={(page) => {
              searchBreedingValues(values, { page });
            }}
            page={rangQuery.result}
            previousPageLabel={t("pagination.previousPage")}
            nextPageLabel={t("pagination.nextPage")}
          />
        </Card>
      </form>
      <PrintContent
        orientation="landscape"
        customCss={`
                     table {
                      font-size: 10px;
                      text-align: center;
                      vertical-align: middle;
                      }
                    `}>
        <RankTable />
      </PrintContent>
    </>
  );
};

export default BreedingValuesRankPage;
